import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type AlertModal = {
  isOpen: boolean;
  message: string;
  title: string;
};

type AlertModalInput = {
  message: string;
  title: string;
};

export type NavGroups = {
  bookings: boolean;
  clubOperation: boolean;
  developer: boolean;
  management: boolean;
  members: boolean;
  notifications: boolean;
  org: boolean;
  report: boolean;
  settings: boolean;
};

interface States {
  alertModal: AlertModal;
  appPath: string;
  greeting: Greetings;
  isCallingBlockingApi: boolean;
  isShowCoachRedirectModal: boolean;
  isShowDrawer: boolean;
  navGroups: NavGroups;
}

type Greetings = 'Good morning' | 'Good afternoon' | 'Good evening' | null;

const initialState: States = {
  alertModal: {
    isOpen: false,
    message: '',
    title: '',
  },
  appPath: '',
  greeting: null,
  isCallingBlockingApi: false,
  isShowCoachRedirectModal: false,
  isShowDrawer: false,
  navGroups: {
    bookings: true,
    clubOperation: true,
    developer: true,
    management: true,
    members: true,
    notifications: true,
    org: true,
    report: true,
    settings: true,
  },
};

export const uiSlice = createSlice({
  initialState,
  name: 'ui',
  reducers: {
    closeAlertModal: (state) => {
      state.alertModal = { ...initialState.alertModal };
    },
    logout: (state) => {
      state.isCallingBlockingApi = false;
      state.alertModal = { ...initialState.alertModal };
    },
    openAlertModal: (state, { payload }: PayloadAction<AlertModalInput>) => {
      state.alertModal = {
        isOpen: true,
        message: payload.message,
        title: payload.title,
      };
    },
    setAppPath: (state, { payload }: PayloadAction<string>) => {
      state.appPath = payload;
    },
    setGreeting: (state, { payload }: PayloadAction<Greetings>) => {
      state.greeting = payload;
    },
    setIsCallingBlockingApi: (state, { payload }: PayloadAction<boolean>) => {
      state.isCallingBlockingApi = payload;
    },
    setIsShowCoachRedirectModal: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isShowCoachRedirectModal = payload;
    },
    setIsShowDrawer: (state, { payload }: PayloadAction<boolean>) => {
      state.isShowDrawer = payload;
    },
    setNavGroups: (state, { payload }: PayloadAction<NavGroups>) => {
      state.navGroups = payload;
    },
    toggleDrawer: (state) => {
      state.isShowDrawer = !state.isShowDrawer;
    },
    toggleNavGroup: (state, { payload }: PayloadAction<keyof NavGroups>) => {
      const key = payload;
      state.navGroups[key] = !state.navGroups[key];

      localStorage.setItem(
        'uiConfig',
        JSON.stringify({ navGroups: state.navGroups }),
      );
    },
  },
});

export default uiSlice.reducer;

export const {
  closeAlertModal: closeAlertModalAction,
  logout: logoutAction,
  openAlertModal: openAlertModalAction,
  setAppPath: setAppPathAction,
  setGreeting: setGreetingAction,
  setIsCallingBlockingApi: setIsCallingBlockingApiAction,
  setIsShowCoachRedirectModal: setIsShowCoachRedirectModalAction,
  setIsShowDrawer: setIsShowDrawerAction,
  setNavGroups: setNavGroupsAction,
  toggleDrawer: toggleDrawerAction,
  toggleNavGroup: toggleNavGroupAction,
} = uiSlice.actions;
